import {
  hasAnyPermission,
  hasBillerRole,
  hasManagerRole,
  hasProviderOrScribeRole,
  hasRequiredPermissions,
} from "@/permission/utils/practitioner-permissions";

import type {
  DestinationAccess,
  DestinationAudience,
  DestinationDisableGate,
  DestinationGates,
  DestinationRequiredRole,
  DestinationState,
} from "../types/destinations";

/**
 * Decides whether a user sees a destination, sees it disabled, or does not see it.
 * Hide gates (audience, features, permissions, role) run before the disable gate, so a destination
 * the user can't see is never reported as merely disabled.
 *
 * @param gates - The destination's gates; `undefined` means practitioners only, with no other gate.
 * @param access - Facts about the current user.
 * @returns `hidden` when any hide gate fails, `disabled` when the disable gate applies, else `visible`.
 */
export function resolveDestinationState(
  gates: DestinationGates | undefined,
  access: DestinationAccess,
): DestinationState {
  if (isHiddenFor(gates ?? {}, access)) return "hidden";
  if (gates?.disabledWhen && DISABLE_GATE_CHECKS[gates.disabledWhen](access)) return "disabled";
  return "visible";
}

/** Whether any hide gate fails for this user. */
function isHiddenFor(gates: DestinationGates, access: DestinationAccess): boolean {
  const audiences = gates.audiences ?? DEFAULT_AUDIENCES;
  if (!audiences.includes(access.audience)) return true;

  const { practiceFeatures = [], developmentFeatures = [] } = gates;
  if (!practiceFeatures.every((feature) => access.practiceFeatures.includes(feature))) return true;
  if (!developmentFeatures.every((feature) => access.developmentFeatures.includes(feature))) {
    return true;
  }

  const { requiredPermissions } = gates;
  if (requiredPermissions && !hasRequiredPermissions(access.permissions, requiredPermissions)) {
    return true;
  }

  return !hasRequiredRole(gates.requiredRole, access);
}

/** Whether the user holds the role a destination requires; `undefined` requires none. */
function hasRequiredRole(
  requiredRole: DestinationGates["requiredRole"],
  access: DestinationAccess,
): boolean {
  if (requiredRole === undefined) return true;
  return REQUIRED_ROLE_CHECKS[requiredRole](access);
}

/** Audiences that see a destination when its gates don't list any. */
const DEFAULT_AUDIENCES: DestinationAudience[] = ["practitioner"];

/** Each role check returns `true` when the user holds that role. */
const REQUIRED_ROLE_CHECKS: Record<DestinationRequiredRole, AccessCheck> = {
  "any-permission": (access) => hasAnyPermission(access.permissions),
  "provider-or-scribe": (access) => hasProviderOrScribeRole(access.permissions),
  "project-admin": (access) => access.isProjectAdmin,
};

/** Each disable check returns `true` when its gate disables the destination for this user. */
const DISABLE_GATE_CHECKS: Record<DestinationDisableGate, AccessCheck> = {
  "non-manager": (access) => !hasManagerRole(access.permissions),
  "non-biller": (access) => !hasBillerRole(access.permissions),
  // An unresolved lab-integration lookup counts as inactive.
  "labs-inactive": (access) => access.isLabOrdersIntegrationActive !== true,
};

type AccessCheck = (access: DestinationAccess) => boolean;
